import React, { useRef, useState, useEffect } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { LICENCE_KEY } from "../constants/charts";
import {functions} from "../constants/common-functions";


am4core.addLicense(LICENCE_KEY);

export default function TimetableChart({
  chartId,
  chartData = [],
  categoryKey = "name",
  type = "column",
  whiteChart = false,
  color,
  width = "100%",
  height = 280,
  onClick,
  showExams,
  isSizeBand
}) {
  const holderRef = useRef(null);
  const chartRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  
  const sortSizeBand = (chartData) => {
	const order = [
		    "1 - 4",
		    "5 - 9",
		    "10 - 19",
		    "20 - 39",
		    "40 - 59",
		    "60 - 89",
		    "90 - 149",
		    "150 - 199",
		    "200 - 249",
		    "250 - 349",
		    "350 - 449",
		    "500+"
		  ];
		
		  chartData.sort((a, b) => {
		    const ai = order.indexOf(a[categoryKey]);
		    const bi = order.indexOf(b[categoryKey]);
		    if (ai === -1 && bi === -1) return 0;
		    if (ai === -1) return 1;
		    if (bi === -1) return -1;
		    return ai - bi;
		  });
  } ;

  // --- Shared chart builder (main + modal) ---
  const buildChart = (target, chartHeight = height) => {
    if (!target) return;

    const chart = am4core.create(target, am4charts.XYChart);
    
    if (isSizeBand) {
		 sortSizeBand(chartData);
	}
    
    chart.data = chartData;
    chart.paddingRight = 20;
    chart.fontSize = 11;
    
    if (type === "column") {
		  chart.paddingTop = 30;  // extra headroom for tall value labels
		} else {
		  chart.paddingRight = 40; // extra space for longest bar label
		}
		
	

    const textColor = am4core.color(whiteChart ? "#fff" : "#000");

    let categoryAxis, valueAxis;
    if (type === "column") {
      categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = categoryKey;
	  categoryAxis.renderer.labels.template.rotation = -90;
	  categoryAxis.renderer.labels.template.horizontalCenter = "middle";
	  categoryAxis.renderer.labels.template.verticalCenter = "middle";
      valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    } else {
      categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = categoryKey;
      valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
    }

    categoryAxis.renderer.minGridDistance = 1;
    categoryAxis.renderer.autoGridCount = false;

   	categoryAxis.renderer.axisFills.template.disabled = false;
		categoryAxis.renderer.axisFills.template.fillOpacity = 0.1;
		categoryAxis.renderer.axisFills.template.fill = "grey";
		categoryAxis.renderer.axisFills.template.strokeOpacity = 0;

    categoryAxis.renderer.labels.template.fill = textColor;
    categoryAxis.renderer.labels.template.fontSize = 9;
    valueAxis.renderer.labels.template.fill = textColor;
    valueAxis.renderer.labels.template.fontSize = 9;
    valueAxis.renderer.grid.template.strokeOpacity = 0;

	valueAxis.renderer.grid.template.disabled = true;
	categoryAxis.renderer.grid.template.disabled = true;

    // Show labels only for modal / larger charts
    const showLabels = chartHeight > 400;
	
	let index = 0;
    const makeSeries = (field, name, fillColor) => {
      const s = chart.series.push(new am4charts.ColumnSeries());
      if (type === "column") {
        s.dataFields.valueY = field;
        s.dataFields.categoryX = categoryKey;
      } else {
        s.dataFields.valueX = field;
        s.dataFields.categoryY = categoryKey;
        s.columns.template.width = am4core.percent(70);
      }

      s.name = name;
	  let gradient = new am4core.LinearGradient();
	  gradient.addColor(am4core.color(functions.adjustColorForChartGradient(color, 20)));
	  gradient.addColor(am4core.color(color));  // base
	  gradient.addColor(am4core.color(functions.adjustColorForChartGradient(color, -20)));
	  
	  if(index === 1){
		gradient = new am4core.LinearGradient();
	    gradient.addColor(am4core.color('#0088bb'));
		gradient.addColor(am4core.color('#006699'));
	  	gradient.addColor(am4core.color('#004477'));
	  }
	  index++;
	  
	  if (type === "column") {
	  	gradient.rotation = 180; // vertical gradient
	 }
	 else{
		gradient.rotation = 90; // vertical gradient
	 }

	  s.columns.template.fill = gradient;
	  s.columns.template.stroke = gradient;
      s.columns.template.strokeWidth = 0;
      s.columns.template.fillOpacity = 0.9;
	  s.columns.template.cornerRadiusTopLeft = 6;
	  s.columns.template.cornerRadiusTopRight = 6;
      s.tooltipText = `{${categoryKey}}: [bold]{${field}}[/]`;
      s.stacked = true;

      if (showLabels) {
			  const label = s.bullets.push(new am4charts.LabelBullet());
			  label.label.text = type === "column" ? "{valueY}" : "{valueX}";
			  label.label.fill = textColor;
			  label.label.fontSize = 11;
			  label.label.truncate = false;
			  label.label.hideOversized = false;
			
			  if (type === "column") {
			    // Rotate vertically for column charts
			    label.label.rotation = 90;
			    label.label.horizontalCenter = "middle";
			    label.label.verticalCenter = "bottom";
			    label.label.dy = -5; // small gap above the column
			  } else {
			    // Keep flat for bar charts
			    label.label.rotation = 0;
			    label.label.dx = 10; // offset right from the bar
			    label.label.verticalCenter = "middle";
			    label.label.horizontalCenter = "left";
			  }
			}

      if (onClick) {
        s.columns.template.events.on("hit", ev => {
          const idx = ev?.target?.dataItem?.index;
          if (idx != null) onClick(idx);
        });
      }
      return s;
    };

    makeSeries("value", "Lectures", color);

    if (showExams) {
      makeSeries("exams", "Exams", "#007bff");
      chart.legend = new am4charts.Legend();
      chart.legend.position = "bottom";
      chart.legend.labels.template.fill = textColor;
      chart.legend.markers.template.width = 10;
      chart.legend.markers.template.height = 10;
    }

    chart.cursor = new am4charts.XYCursor();
    chart.appear(800, 100);
    
    return chart;
  };

  // --- Build chart once on mount ---
  useEffect(() => {
    if (!holderRef.current) return;

    // Kill old chart
    if (chartRef.current) {
      chartRef.current.dispose();
      chartRef.current = null;
    }

    // Rebuild with current props (including showExams)
    chartRef.current = buildChart(holderRef.current);

    return () => chartRef.current && chartRef.current.dispose();

  }, [showExams, type, whiteChart, isSizeBand]);
		
		// --- Update data when chartData changes ---
	useEffect(() => {
	  if (chartRef.current) {
		if (isSizeBand) {
			 sortSizeBand(chartData);
		}
	    chartRef.current.data = chartData;
	    chartRef.current.invalidateRawData();
	  }
	}, [chartData]);
	
	useEffect(() => {
	  if (!chartRef.current && holderRef.current) {
	    chartRef.current = buildChart(holderRef.current);
		if (isSizeBand) {
			 sortSizeBand(chartData);
		}
		console.log("Chart Data:");
		console.log(chartData);
	  }
	}, [chartData]);
	
	// --- Build modal chart when opened ---
	useEffect(() => {
	  let modalChart;
	  if (showModal) {
	    const modalDiv = document.getElementById(`${chartId}-modal`);
	    if (modalDiv) modalChart = buildChart(modalDiv, 600);
	  }
	  return () => modalChart && modalChart.dispose();
	}, [showModal]);


 return (
  <>
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        width,
        height,
        position: "relative"
      }}
    >
      {/* chart takes 95 % width, leaves gap for expand button */}
      <div
        ref={holderRef}
        id={chartId}
        style={{
          width: "calc(100% - 30px)",
          height: "100%"
        }}
      />

      {/* dedicated button area */}
      <div
        style={{
          width: 20,
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          paddingTop: 6
        }}
      >
        <button
          onClick={() => setShowModal(true)}
          style={{
            background: "lightgrey",
            border: "none",
            fontSize: 16,
            cursor: "pointer",
            color: '#fff',
            fontWeight:"bold",
            borderRadius:"15px"
          }}
          title="Expand chart"
        >
         +
        </button>
      </div>
    </div>

    {showModal && (
  <div
    style={{
      position: "fixed",
      top: 80,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.6)",
      zIndex: 9999,
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    }}
    onClick={() => setShowModal(false)}
  >
    <div
      style={{
        background: "#fff",
        padding: 20,
        borderRadius: 8,
        width: "90%",
        height: "80%",
        position: "relative"
      }}
      onClick={e => e.stopPropagation()}
    >
      {/* Close Button with pointerEvents enabled */}
      <div
        style={{
          position: "absolute",
          top: 8,
          right: 12,
          zIndex: 30000,
          pointerEvents: "auto"
        }}
      >
        <button
          onClick={() => setShowModal(false)}
          style={{
            border: "none",
            background: "black",
            fontSize: 13,
            width: 30,
            height: 30,
            borderRadius: "15px",
            fontWeight: "bold",
            color: "#fff",
            cursor: "pointer"
          }}
          title="Close"
        >
          X
        </button>
      </div>

      {/* Chart container - clicks disabled so it cant eat the button */}
      <div
        id={`${chartId}-modal`}
        style={{
          width: "100%",
          height: "100%",
          zIndex: 100,
          pointerEvents: "none"
        }}
      />
    </div>
  </div>
)}
  </>
);


}
